import React, { useState, useEffect } from "react";
import { useParams, useNavigate } from "react-router-dom";
import {
  Box,
  Paper,
  Text,
  Button,
  Group,
  TextInput,
  NumberInput,
  Textarea,
  Stack,
  LoadingOverlay,
  Grid,
  Divider,
  SimpleGrid,
  Image,
  ActionIcon,
  Tooltip,
  Badge,
  Card,
} from "@mantine/core";
import {
  IconArrowLeft,
  IconDeviceFloppy,
  IconTrash,
  IconStarFilled,
  IconStar,
  IconUpload,
} from "@tabler/icons-react";
import { useNotification } from "../adminProjectNotifications/popupnotifications";
import { ImageUploadComponent } from "../adminProductManagement/createProduct";
import { API_BASE_URL } from "../../config/baseUrls";
import { uploadFetch } from "../../utils/directUpload";

const getAuthHeaders = () => ({
  Authorization: `Bearer ${localStorage.getItem("adminToken")}`,
  "Content-Type": "application/json",
});

export const EditProjectPage = () => {
  const { id } = useParams();
  const navigate = useNavigate();
  const { showSuccess, showError } = useNotification();

  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [uploadingMedia, setUploadingMedia] = useState(false);

  const [formData, setFormData] = useState({
    title: "",
    category: "",
    shortDescription: "",
    description: "",
    targetAmount: 0,
    targetQuantity: 0,
    targetVotes: 0,
    unitPrice: 0,
    shippingFee: 0,
    fundraisingEnd: "",
    media: [], // Now handles an array of media objects
    options: [],
  });

  // Fetch existing project data
  useEffect(() => {
    const fetchProjectDetails = async () => {
      try {
        const res = await fetch(
          `${API_BASE_URL}/admin/projects/review/${id}`,
          { headers: getAuthHeaders() },
        );
        const data = await res.json();

        if (data.success) {
          const project = data.data;

          setFormData({
            title: project.title || "",
            category: project.category || "",
            shortDescription: project.shortDescription || "",
            description: project.description || "",
            options: project.options || [],
            targetAmount: Number(project.targetAmount) || 0,
            targetQuantity: Number(project.targetQuantity) || 0,
            targetVotes: Number(project.targetVotes) || 0,
            unitPrice: Number(project.unitPrice) || 0,
            shippingFee: Number(project.shippingFee) || 0,
            fundraisingEnd: project.fundraisingEnd
              ? new Date(project.fundraisingEnd).toISOString().split("T")[0]
              : "",
            // Map the media array to ensure it's ready for editing
            media: project.media || [],
          });
        } else {
          throw new Error(data.error?.message || "Failed to load project");
        }
      } catch (e) {
        showError("Error", "Could not load project details.");
        navigate(-1);
      } finally {
        setLoading(false);
      }
    };

    if (id) fetchProjectDetails();
  }, [id, navigate, showError]);

  const handleChange = (field, value) => {
    setFormData((prev) => ({ ...prev, [field]: value }));
  };

  // --- MEDIA MANAGEMENT LOGIC ---

  const handleFileUpload = async (e) => {
    const file = e.target.files[0];
    if (!file) return;

    setUploadingMedia(true);
    const reader = new FileReader();
    reader.readAsDataURL(file);

    reader.onload = async () => {
      const base64 = reader.result.split(",")[1];
      try {
        const res = await uploadFetch(
          `${API_BASE_URL}/auth/uploadImage`,
          {
            method: "POST",
            headers: getAuthHeaders(),
            body: JSON.stringify({ image: base64, name: file.name }),
          },
        );
        const data = await res.json();
        if (data.url) {
          setFormData((prev) => {
            const currentMedia = prev.media || [];
            const newMediaItem = {
              url: data.url,
              // If this is the first image being uploaded, make it primary automatically
              isPrimary: currentMedia.length === 0,
              mediaType: "IMAGE",
            };
            return { ...prev, media: [...currentMedia, newMediaItem] };
          });
        }
      } catch (err) {
        showError("Upload Failed", "Could not upload image to server.");
      } finally {
        setUploadingMedia(false);
      }
    };
  };

  const removeMedia = (indexToRemove) => {
    setFormData((prev) => {
      const updatedMedia = prev.media.filter((_, i) => i !== indexToRemove);

      // If we deleted the primary image, randomly assign the first remaining image as primary
      if (prev.media[indexToRemove].isPrimary && updatedMedia.length > 0) {
        updatedMedia[0].isPrimary = true;
      }

      return { ...prev, media: updatedMedia };
    });
  };

  const setPrimaryMedia = (indexToSet) => {
    setFormData((prev) => {
      const updatedMedia = prev.media.map((item, i) => ({
        ...item,
        isPrimary: i === indexToSet,
      }));
      return { ...prev, media: updatedMedia };
    });
  };

  // --- SAVE LOGIC ---

  const handleAddOption = () => {
    setFormData((prev) => ({
      ...prev,
      options: [
        ...prev.options,
        {
          id: `temp-${Date.now()}`,
          optionName: "",
          description: "",
          imageUrl: "",
        },
      ],
    }));
  };

  const handleOptionChange = (index, field, value) => {
    setFormData((prev) => {
      const newOptions = [...prev.options];
      newOptions[index][field] = value;
      return { ...prev, options: newOptions };
    });
  };

  const handleRemoveOption = (index) => {
    setFormData((prev) => ({
      ...prev,
      options: prev.options.filter((_, i) => i !== index),
    }));
  };

  const handleSave = async () => {
    setSaving(true);
    try {
      const res = await fetch(
        `${API_BASE_URL}/admin/projects/${id}`,
        {
          method: "PUT",
          headers: getAuthHeaders(),
          body: JSON.stringify(formData),
        },
      );
      const data = await res.json();

      if (data.success) {
        showSuccess("Saved", "Project updated successfully.");
        navigate(`/admin/dashboard/project-management/review/${id}`);
      } else {
        showError("Update Failed", data.error?.message || "Unknown error");
      }
    } catch (e) {
      showError("Error", "Network request failed while saving.");
    } finally {
      setSaving(false);
    }
  };

  if (loading) return <LoadingOverlay visible />;

  return (
    <Box pos="relative">
      <Group justify="space-between" mb="lg">
        <Button
          variant="subtle"
          leftSection={<IconArrowLeft size={16} />}
          onClick={() => navigate(-1)}
        >
          Cancel
        </Button>
        <Button
          color="blue"
          leftSection={<IconDeviceFloppy size={16} />}
          loading={saving}
          onClick={handleSave}
        >
          Save Changes
        </Button>
      </Group>

      <Text size="xl" fw={800} mb="md">
        Edit Project Details
      </Text>

      <Grid gutter="lg">
        {/* LEFT COLUMN: Main Text Details */}
        <Grid.Col span={{ base: 12, md: 8 }}>
          <Stack gap="md">
            <Paper withBorder radius="md" p="xl">
              <Text fw={600} mb="md" size="lg">
                Basic Information
              </Text>
              <Stack gap="md">
                <TextInput
                  label="Project Title"
                  value={formData.title}
                  onChange={(e) => handleChange("title", e.target.value)}
                  required
                />
                <Group grow>
                  <TextInput
                    label="Category"
                    value={formData.category}
                    onChange={(e) => handleChange("category", e.target.value)}
                  />
                  <TextInput
                    label="Fundraising End Date"
                    type="date"
                    value={formData.fundraisingEnd}
                    onChange={(e) =>
                      handleChange("fundraisingEnd", e.target.value)
                    }
                  />
                </Group>
              </Stack>
            </Paper>

            <Paper withBorder radius="md" p="xl">
              <Text fw={600} mb="md" size="lg">
                Descriptions
              </Text>
              <Stack gap="md">
                <Textarea
                  label="Short Description (Appears on cards)"
                  value={formData.shortDescription}
                  onChange={(e) =>
                    handleChange("shortDescription", e.target.value)
                  }
                  rows={2}
                />
                <Textarea
                  label="Full Description"
                  value={formData.description}
                  onChange={(e) => handleChange("description", e.target.value)}
                  rows={8}
                />
              </Stack>
            </Paper>

            <Paper withBorder radius="md" p="xl">
              <Text fw={600} mb="md" size="lg">
                Targets & Pricing
              </Text>
              <Stack gap="md">
                <Group grow>
                  <NumberInput
                    label="Target Amount (₹)"
                    value={formData.targetAmount}
                    onChange={(val) => handleChange("targetAmount", val)}
                    min={0}
                  />
                  <NumberInput
                    label="Target Quantity"
                    value={formData.targetQuantity}
                    onChange={(val) => handleChange("targetQuantity", val)}
                    min={0}
                  />
                  <NumberInput
                    label="Target Votes"
                    value={formData.targetVotes}
                    onChange={(val) => handleChange("targetVotes", val)}
                    min={0}
                  />
                </Group>
                <Divider my="sm" />
                <Group grow>
                  <NumberInput
                    label="Unit Price (₹)"
                    value={formData.unitPrice}
                    onChange={(val) => handleChange("unitPrice", val)}
                    min={0}
                  />
                  <NumberInput
                    label="Shipping Fee (₹)"
                    value={formData.shippingFee}
                    onChange={(val) => handleChange("shippingFee", val)}
                    min={0}
                  />
                </Group>
              </Stack>
            </Paper>

            <Paper withBorder radius="md" p="xl">
              <Group justify="space-between" mb="md">
                <Text fw={600} size="lg">
                  Project Options / Variants
                </Text>
                <Button variant="light" size="sm" onClick={handleAddOption}>
                  + Add Option
                </Button>
              </Group>

              <Stack gap="md">
                {formData.options.map((opt, index) => (
                  <Paper key={opt.id} withBorder p="md" bg="gray.0">
                    <Group align="flex-start" wrap="nowrap">
                      {/* Option Image Uploader */}
                      <Box w={120} style={{ flexShrink: 0 }}>
                        <ImageUploadComponent
                          existingUrl={opt.imageUrl || ""}
                          onUpload={(url) =>
                            handleOptionChange(index, "imageUrl", url)
                          }
                        />
                      </Box>

                      {/* Option Text Fields */}
                      <Stack gap="sm" style={{ flex: 1 }}>
                        <Group justify="space-between" align="center">
                          <TextInput
                            placeholder="Option Name (e.g., Red Version)"
                            value={opt.optionName || ""} // 👈 FIX: Added fallback
                            onChange={(e) =>
                              handleOptionChange(
                                index,
                                "optionName",
                                e.target.value,
                              )
                            }
                            style={{ flex: 1 }}
                            required
                          />
                          <ActionIcon
                            color="red"
                            variant="subtle"
                            onClick={() => handleRemoveOption(index)}
                          >
                            <IconTrash size={18} />
                          </ActionIcon>
                        </Group>
                        <Textarea
                          placeholder="Option Description..."
                          value={opt.description || ""} // 👈 FIX: Added fallback to prevent React 'null' errors
                          onChange={(e) =>
                            handleOptionChange(
                              index,
                              "description",
                              e.target.value,
                            )
                          }
                          rows={2}
                        />

                        {/* Show locked stats if it's an existing option */}
                        {/* {opt.id && !opt.id.toString().startsWith("temp-") && (
            <Group gap="xs">
              <Badge color="blue" variant="light">
                {opt.voteCount || 0} Votes
              </Badge>
              <Badge color="green" variant="light">
                {opt.pledgeCount || 0} Pledges
              </Badge>
            </Group>
          )} */}
                      </Stack>
                    </Group>
                  </Paper>
                ))}

                {formData.options.length === 0 && (
                  <Text c="dimmed" ta="center" py="sm">
                    No options added yet.
                  </Text>
                )}
              </Stack>
            </Paper>
          </Stack>
        </Grid.Col>

        {/* RIGHT COLUMN: Multiple Media Gallery */}
        <Grid.Col span={{ base: 12, md: 4 }}>
          <Stack gap="md">
            <Paper withBorder radius="md" p="xl">
              <Group justify="space-between" mb="xs">
                <Text fw={600} size="lg">
                  Media Gallery
                </Text>
                <Badge>{formData.media.length} Files</Badge>
              </Group>
              <Text size="sm" c="dimmed" mb="lg">
                Upload images for this project. Use the star icon to select the
                primary cover image.
              </Text>

              {/* Media Grid */}
              <SimpleGrid cols={2} spacing="md" mb="md">
                {formData.media.map((item, index) => (
                  <Card key={index} p={0} radius="md" withBorder pos="relative">
                    <Image src={item.url} h={120} fit="cover" />

                    {/* Primary Badge Overlay */}
                    {item.isPrimary && (
                      <Badge
                        color="yellow"
                        variant="filled"
                        pos="absolute"
                        top={8}
                        left={8}
                        size="xs"
                      >
                        Cover
                      </Badge>
                    )}

                    {/* Action Overlay */}
                    <Group
                      pos="absolute"
                      bottom={8}
                      right={8}
                      gap="xs"
                      style={{
                        backgroundColor: "rgba(255,255,255,0.9)",
                        padding: "4px",
                        borderRadius: "8px",
                      }}
                    >
                      <Tooltip label="Set as Primary Cover">
                        <ActionIcon
                          size="sm"
                          variant="subtle"
                          color={item.isPrimary ? "yellow" : "gray"}
                          onClick={() => setPrimaryMedia(index)}
                        >
                          {item.isPrimary ? (
                            <IconStarFilled size={16} />
                          ) : (
                            <IconStar size={16} />
                          )}
                        </ActionIcon>
                      </Tooltip>
                      <Tooltip label="Delete Image">
                        <ActionIcon
                          size="sm"
                          variant="subtle"
                          color="red"
                          onClick={() => removeMedia(index)}
                        >
                          <IconTrash size={16} />
                        </ActionIcon>
                      </Tooltip>
                    </Group>
                  </Card>
                ))}

                {/* Upload Button Block */}
                <Button
                  component="label"
                  variant="outline"
                  color="gray"
                  h={120}
                  style={{ borderStyle: "dashed" }}
                  loading={uploadingMedia}
                >
                  <Stack gap={0} align="center">
                    <IconUpload size={24} />
                    <Text size="xs" mt={4}>
                      Add Media
                    </Text>
                  </Stack>
                  <input
                    type="file"
                    hidden
                    accept="image/*"
                    onChange={handleFileUpload}
                  />
                </Button>
              </SimpleGrid>
            </Paper>
          </Stack>
        </Grid.Col>
      </Grid>
    </Box>
  );
};
